/**
 * Admin — Store Update Manifest editor.
 *
 * Edits the R2-hosted manifest that drives the in-app store-update nudges. See
 * `src/types/store-update.ts` for the tier resolution rules, and
 * `services/store-update/store-update.service.ts` for the R2 read/write path.
 *
 * The form loads the current manifest (or an empty template on 404) via
 * `GET /api/admin/store-updates`, and saves via `PUT /api/admin/store-updates`. The server
 * enforces the ordering invariant (mandatory <= recommended <= latest); we surface those
 * errors verbatim.
 *
 * There is a "live tier preview" — the admin types a hypothetical running version, and the
 * page shows which tier a device on that version would land in. This is the single most
 * useful validation surface: a misordered save prompts a modal on production users, and the
 * preview catches it before Save.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/config/app-store-links';
import {
  resolveTier,
  STORE_UPDATE_PLATFORMS,
  StoreUpdateManifest,
  StoreUpdatePlatform,
  StoreUpdatePlatformPolicy,
  StoreUpdateTier
} from '@/types/store-update';

const EMPTY_POLICY: StoreUpdatePlatformPolicy = {
  latestVersion: '',
  minRecommendedVersion: '',
  minMandatoryVersion: '',
  storeUrl: ''
};

/**
 * Store URLs come from `src/config/app-store-links.ts` — the same source `AppStoreBadges` uses.
 * If those URLs ever change, the admin form's defaults update automatically.
 */
const DEFAULT_STORE_URLS: Record<StoreUpdatePlatform, string> = {
  ios: APP_STORE_URL,
  android: PLAY_STORE_URL
};

/** All fields present + non-empty. The server validates SemVer/URL shape; we only gate Save. */
function isPolicyComplete(p: StoreUpdatePlatformPolicy | undefined): p is StoreUpdatePlatformPolicy {
  return Boolean(
    p &&
      p.latestVersion.trim() &&
      p.minRecommendedVersion.trim() &&
      p.minMandatoryVersion.trim() &&
      p.storeUrl.trim()
  );
}

const TIER_LABELS: Record<StoreUpdateTier, { label: string; className: string }> = {
  none: { label: 'No nudge', className: 'bg-slate-100 text-slate-700' },
  optional: { label: 'Dismissible banner', className: 'bg-blue-100 text-blue-800' },
  recommended: { label: 'Sticky banner', className: 'bg-amber-100 text-amber-900' },
  mandatory: { label: 'Blocking modal', className: 'bg-red-100 text-red-900' }
};

function PolicyEditor({
  platform,
  policy,
  onChange
}: {
  platform: StoreUpdatePlatform;
  policy: StoreUpdatePlatformPolicy;
  onChange: (next: StoreUpdatePlatformPolicy) => void;
}) {
  const label = platform === 'ios' ? 'iOS' : 'Android';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Latest version</span>
          <Input
            value={policy.latestVersion}
            onChange={(e) => onChange({ ...policy, latestVersion: e.target.value })}
            placeholder="1.2.3"
            className="mt-1"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Recommended floor <span className="text-gray-500">(sticky banner below this)</span>
          </span>
          <Input
            value={policy.minRecommendedVersion}
            onChange={(e) => onChange({ ...policy, minRecommendedVersion: e.target.value })}
            placeholder="1.2.0"
            className="mt-1"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Mandatory floor <span className="text-gray-500">(blocking modal below this)</span>
          </span>
          <Input
            value={policy.minMandatoryVersion}
            onChange={(e) => onChange({ ...policy, minMandatoryVersion: e.target.value })}
            placeholder="1.0.0"
            className="mt-1"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Store URL</span>
          <Input
            value={policy.storeUrl}
            onChange={(e) => onChange({ ...policy, storeUrl: e.target.value })}
            placeholder={DEFAULT_STORE_URLS[platform]}
            className="mt-1"
          />
        </label>
      </CardContent>
    </Card>
  );
}

function TierPreview({
  policy,
  runningVersion
}: {
  policy: StoreUpdatePlatformPolicy | undefined;
  runningVersion: string;
}) {
  const tier = resolveTier(runningVersion.trim() || undefined, policy);
  const style = TIER_LABELS[tier];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${style.className}`}>
      {style.label}
    </span>
  );
}

export default function AdminStoreUpdatesPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [ios, setIos] = useState<StoreUpdatePlatformPolicy>({ ...EMPTY_POLICY });
  const [android, setAndroid] = useState<StoreUpdatePlatformPolicy>({ ...EMPTY_POLICY });
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [previewVersion, setPreviewVersion] = useState('1.0.0');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await api.get<{ manifest: StoreUpdateManifest | null }>(
          '/api/admin/store-updates'
        );
        if (cancelled) return;
        const manifest = response.manifest;
        if (manifest) {
          if (manifest.ios) setIos({ ...EMPTY_POLICY, ...manifest.ios });
          if (manifest.android) setAndroid({ ...EMPTY_POLICY, ...manifest.android });
          setUpdatedAt(manifest.updatedAt);
        } else {
          // First-time setup — seed the store URLs so admin doesn't have to look them up.
          setIos((prev) => ({ ...prev, storeUrl: DEFAULT_STORE_URLS.ios }));
          setAndroid((prev) => ({ ...prev, storeUrl: DEFAULT_STORE_URLS.android }));
        }
      } catch (error: any) {
        showError('Failed to load manifest', error?.message ?? 'Unknown error');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  const canSave = useMemo(() => {
    // Save if at least one platform is fully filled in. Partial platforms are dropped
    // server-side by omission — an admin who only wants to set iOS shouldn't be forced to fill
    // Android too.
    return isPolicyComplete(ios) || isPolicyComplete(android);
  }, [ios, android]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: StoreUpdateManifest = {};
      if (isPolicyComplete(ios)) payload.ios = ios;
      if (isPolicyComplete(android)) payload.android = android;

      const response = await api.put<{ manifest: StoreUpdateManifest }>(
        '/api/admin/store-updates',
        payload
      );
      setUpdatedAt(response.manifest.updatedAt);
      showSuccess('Manifest saved', 'Devices will pick it up on their next resume.');
    } catch (error: any) {
      // Backend validation errors come back as { error, message } on 400. Surface message.
      // The api client wraps them on `ApiError.data`, not on `response.data` (axios shape).
      const message =
        error?.data?.message ?? error?.message ?? 'Failed to save manifest';
      showError('Save failed', message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
              Back to Admin
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Store Update Manifest</h1>
            <p className="text-sm text-gray-600 mt-1">
              Controls the in-app "please update from the store" nudges. Edits propagate to
              devices on their next foreground resume (within ~1 minute of save).
            </p>
            {updatedAt && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated {new Date(updatedAt).toLocaleString()}
              </p>
            )}
          </div>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="w-4 h-4 mr-2" aria-hidden="true" />
            )}
            Save
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {STORE_UPDATE_PLATFORMS.map((platform) => (
            <PolicyEditor
              key={platform}
              platform={platform}
              policy={platform === 'ios' ? ios : android}
              onChange={platform === 'ios' ? setIos : setAndroid}
            />
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tier preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                A device running version…
              </span>
              <Input
                value={previewVersion}
                onChange={(e) => setPreviewVersion(e.target.value)}
                placeholder="1.0.0"
                className="mt-1 max-w-xs"
              />
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                <span className="text-sm text-gray-700">iOS</span>
                <TierPreview
                  policy={isPolicyComplete(ios) ? ios : undefined}
                  runningVersion={previewVersion}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                <span className="text-sm text-gray-700">Android</span>
                <TierPreview
                  policy={isPolicyComplete(android) ? android : undefined}
                  runningVersion={previewVersion}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Ordering rule: mandatory floor ≤ recommended floor ≤ latest. The server refuses
              saves that violate it — check the preview before saving.
            </p>
          </CardContent>
        </Card>

        {/*
          Dev-only visual preview. Opens the dashboard in a new tab with a URL param that
          `useStoreUpdate` picks up and synthesises the corresponding tier for. Not shown in
          production builds (the whole block tree-shakes out via `import.meta.env.DEV`).
        */}
        {import.meta.env.DEV && (
          <Card>
            <CardHeader>
              <CardTitle>Preview the banner (dev only)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">
                Open a new tab with a synthetic tier so you can see how each nudge actually looks
                over real content. These buttons are hidden in production builds.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.open('/dashboard?storeUpdatePreview=optional', '_blank', 'noopener,noreferrer')
                  }
                >
                  Dismissible banner
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.open('/dashboard?storeUpdatePreview=recommended', '_blank', 'noopener,noreferrer')
                  }
                >
                  Sticky banner
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.open('/dashboard?storeUpdatePreview=mandatory', '_blank', 'noopener,noreferrer')
                  }
                >
                  Blocking modal
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Dismissible reacts to the X / Later buttons (state-only, resets on refresh).
                Sticky and mandatory ignore dismissal by design — matches production semantics.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
